import { useMountEffect, useSyncedRef, useTimeoutEffect } from "@react-hookz/web";
import { useCallback, useEffect, useRef, useState } from "react";
import type { PopupContentType } from "scmp-sdk/components/acquisition-popup-container/types";
import { useSdkContext } from "scmp-sdk/components/contexts";

import { addDays, isAfterOrEqual, isValid, notEmpty, safeSetLocalStorage } from "shared/lib/utils";

import { getPopupShownTimestamp, isPopupExpired, setPopupShownTimestampSession } from "./helpers";
import type { PopRenderPayload, PopupGetter, useRenderPopupOptions } from "./types";

export const useRenderPopup = (
  popupGetters: Partial<{
    [K in PopupContentType]: PopupGetter;
  }>,
  renderPopupOptions: useRenderPopupOptions,
) => {
  const {
    acquisition: { popupState },
    currentRouteWithoutQuerystring,
  } = useSdkContext();
  const { setWithTTL } = usePopupLocalStorage();
  const [popupDelayElapsed, setPopupDelayElapsed] = useState(false);
  const [popup, setPopup] = useState<PopRenderPayload>();
  const isPopupSet = useRef(false);
  const isPopupSetFromActionGetters = useRef(false);
  const popupReference = useSyncedRef(popup);
  // https://www.seroundtable.com/google-cumulative-layout-shift-metric-update-31233.html
  // delay for 5 seconds to show popups
  const globalPopupDelay = 5000; // ms

  useTimeoutEffect(() => {
    setPopupDelayElapsed(true);
  }, globalPopupDelay);

  const triggerPopup = useCallback(() => {
    const fromActionGetters = Object.values(popupGetters).flatMap(
      getter => getter.getNextFromAction ?? [],
    );
    for (const getter of fromActionGetters) {
      const handleClose = () => {
        if (nextPopup?.localStorageKey && !nextPopup.updateLocalStorageOnShow) {
          setWithTTL(nextPopup.localStorageKey);
        }
        setPopup(undefined);
      };
      const nextPopup = getter(handleClose);
      if (nextPopup) {
        isPopupSetFromActionGetters.current = true;
        setPopup(nextPopup);

        if (nextPopup.updateLocalStorageOnShow) setWithTTL(nextPopup.localStorageKey);
        return;
      }
    }

    if (!popupDelayElapsed || renderPopupOptions.skipTriggerFromConfig) return;

    const timestamp = getPopupShownTimestamp();

    if (timestamp !== null && !isPopupExpired(timestamp)) return;

    const fromConfigGetters = Object.values(popupGetters).map(getter => getter.getNextFromConfig);
    for (const getter of fromConfigGetters) {
      const handleClose = () => {
        if (nextPopup?.localStorageKey && !nextPopup.updateLocalStorageOnShow) {
          setWithTTL(nextPopup.localStorageKey);
        }
        isPopupSet.current = false;
        setPopup(undefined);
      };
      const nextPopup = getter(handleClose);
      if (nextPopup) {
        isPopupSet.current = true;
        setTimeout(
          () => {
            setPopup(nextPopup);
            setPopupShownTimestampSession();
            if (nextPopup.updateLocalStorageOnShow) setWithTTL(nextPopup.localStorageKey);
          },
          Math.max((nextPopup.delay ?? 0) - globalPopupDelay, 0),
        );
        return;
      }
    }

    const fromScrollPositionGetters = Object.values(popupGetters).flatMap(
      getter => getter.getNextFromScrollPosition ?? [],
    );
    for (const getter of fromScrollPositionGetters) {
      const handleClose = () => {
        if (nextPopup?.localStorageKey && !nextPopup.updateLocalStorageOnShow) {
          setWithTTL(nextPopup.localStorageKey);
        }
        setPopup(undefined);
      };
      const nextPopup = getter(handleClose);
      if (nextPopup) {
        setPopup(nextPopup);
        setPopupShownTimestampSession();

        if (nextPopup.updateLocalStorageOnShow) setWithTTL(nextPopup.localStorageKey);
        return;
      }
    }
  }, [popupDelayElapsed, popupGetters, setWithTTL, renderPopupOptions.skipTriggerFromConfig]);

  useEffect(() => {
    if (
      isPopupSet.current ||
      isPopupSetFromActionGetters.current ||
      !currentRouteWithoutQuerystring ||
      popupReference.current !== undefined ||
      !Object.values(popupState).some(state => notEmpty(state))
    )
      return;

    triggerPopup();
  }, [currentRouteWithoutQuerystring, popupReference, popupState, triggerPopup]);

  return {
    popup: popupReference.current,
  };
};

const usePopupLocalStorage = () => {
  const setWithTTL = useCallback((popupKey: string) => {
    safeSetLocalStorage(popupKey, addDays(new Date(), 90).toISOString());
  }, []);

  // Housekeeping effect
  useMountEffect(() => {
    for (const [localStorageKey] of Object.entries(localStorage).filter(([localStorageKey]) =>
      ["ACQUISITION", "CAMPAIGN", "TOPIC_PACKAGE"].some(targetKey =>
        localStorageKey.startsWith(targetKey),
      ),
    )) {
      // If value is not date (possibly is legacy value), reset it to be date value
      if (!isValid(new Date(localStorage.getItem(localStorageKey) ?? ""))) {
        setWithTTL(localStorageKey);
        continue;
      }
      const itemIsExpired = isAfterOrEqual(new Date(), localStorage.getItem(localStorageKey) ?? "");
      if (itemIsExpired) localStorage.removeItem(localStorageKey);
    }
  });

  return { setWithTTL };
};
